import { Translator, TranslatorInjectionKey } from '@othercommerce/foundation';
import { isFloat } from '../Support/Math/IsFloat.ts';
import type { Unit } from '../Enums/Bridge';
import { inject, type InjectionKey, type App } from 'vue';
import { resolveFromApp } from '../Support/DependencyInjection/Resolver.ts';

export const UnitFormatterInjectionKey = Symbol('UnitFormatter') as InjectionKey<UnitFormatter>;

export class UnitFormatter {
  private readonly locale: string;
  private readonly translator: Translator;

  constructor(locale: string, translator: Translator) {
    this.locale = locale;
    this.translator = translator;
  }

  public symbol(type: Unit): string {
    return this.translator.trans(`UNIT.${type}.SHORT`);
  }

  public name(type: Unit): string {
    return this.translator.choice(`UNIT.${type}.LONG`, 1);
  }

  public multiplied(multiplied: string | number, type: Unit, value: string | number, options: Intl.NumberFormatOptions = {}): string {
    return multiplied + '&nbsp;' + 'x' + '&nbsp;' + this.short(type, value, options);
  }

  public short(type: Unit, value: string | number, options: Intl.NumberFormatOptions = {}): string {
    let formatted = this.createValueFormatter(options).format(Number(value));
    let key = `UNIT.${type}.SHORT`;

    return formatted + '&nbsp;' + this.translator.trans(key);
  }

  public long(type: Unit, value: string | number, options: Intl.NumberFormatOptions = {}): string {
    let formatted = this.createValueFormatter(options).format(Number(value));
    let key = `UNIT.${type}.LONG`;

    // Gdy wartość posiada miejsca po przecinku zazwyczaj używamy dopełniacza mówiąc na przykład "pół metra".
    if (isFloat(value)) {
      return this.genitive(type, value, options);
    }

    return formatted + '&nbsp;' + this.translator.choice(key, Number(value));
  }

  public genitive(type: Unit, value: string | number, options: Intl.NumberFormatOptions = {}): string {
    let formatted = this.createValueFormatter(options).format(Number(value));
    let key = `UNIT.${type}.LONG_GENITIVE`;

    if (isFloat(value)) {
      value = 1;
    }

    return formatted + '&nbsp;' + this.translator.choice(key, Number(value));
  }

  public accusative(type: Unit, value: string | number, options: Intl.NumberFormatOptions = {}): string {
    let formatted = this.createValueFormatter(options).format(Number(value));
    let key = `UNIT.${type}.LONG_ACCUSATIVE`;

    if (isFloat(value)) {
      return this.genitive(type, value, options);
    }

    return formatted + '&nbsp;' + this.translator.choice(key, Number(value));
  }

  public createValueFormatter(options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
    return new Intl.NumberFormat(this.locale, { style: 'decimal', maximumFractionDigits: 4, ...options });
  }
}

export function useUnitFormatter() {
  return inject(UnitFormatterInjectionKey)!;
}

export function createUnitFormatter(locale: string) {
  return {
    install(app: App) {
      const translator = resolveFromApp(app, TranslatorInjectionKey);
      const formatter = new UnitFormatter(locale, translator);

      app.provide(UnitFormatterInjectionKey, formatter);

      app.config.globalProperties.$unit = formatter;
    },
  };
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $unit: UnitFormatter;
  }
}
